import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Brain, ClipboardList, ArrowRight, Zap, Clock, Target } from "lucide-react";
import CognitiveRadar from "@/components/CognitiveRadar";
import { motion } from "framer-motion";

const SEVERITY_LABEL = { preserved: "Conservado", mild: "Leve", moderate: "Moderado", severe: "Severo" };

export default function PatientDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ pre: null, post: null });
  const [progress, setProgress] = useState([]);
  const [errors, setErrors] = useState({ by_type: {}, by_domain: {}, total_errors: 0 });
  const [review, setReview] = useState({ items: [], focus_domains: [] });
  const [liveDomains, setLiveDomains] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/profile/me").then((r) => setProfile(r.data)),
      api.get("/progress/me").then((r) => setProgress(r.data.sessions || [])),
      api.get("/errors/me").then((r) => setErrors(r.data)),
      api.get("/review/next?limit=3").then((r) => setReview(r.data)).catch(() => {}),
      api.get("/profile/domain-accuracy").then((r) => setLiveDomains(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const nextSession = progress.find((s) => !s.completed) || progress[0];
  const preDone = !!profile.pre;
  const totalCompleted = progress.filter((s) => s.completed).length;
  const overallAcc = profile.pre?.overall_accuracy != null ? Math.round(profile.pre.overall_accuracy * 100) : null;

  return (
    <div className="space-y-8" data-testid="patient-dashboard">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-wrap items-end gap-6 justify-between">
          <div>
            <p className="text-sm uppercase tracking-widest text-primary font-semibold">Hola, {user?.name}</p>
            <h1 className="heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">
              Tu perfil cognitivo-social
            </h1>
            <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
              {preDone
                ? "Aquí tienes tu resumen actualizado. Continúa con las sesiones para consolidar el progreso."
                : "Comienza con la evaluación PRE. Establece tu línea base y personalizamos tu entrenamiento."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!preDone && (
              <Link to="/assessment/pre">
                <Button size="lg" className="rounded-full h-12" data-testid="cta-start-pre">
                  Comenzar evaluación PRE <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            )}
            {nextSession && (
              <Link to={`/sessions/${nextSession.session_id}`}>
                <Button size="lg" variant={preDone ? "default" : "outline"} className="rounded-full h-12" data-testid="cta-continue-session">
                  Continuar sesión {nextSession.session_id}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Stat label="Sesiones completadas" value={`${totalCompleted}/20`} Icon={ClipboardList} />
        <Stat label="Precisión global (PRE)" value={overallAcc != null ? `${overallAcc}%` : "—"} Icon={Target} />
        <Stat label="Errores totales" value={errors.total_errors} Icon={Zap} tone="warn" />
      </div>

      {review.items && review.items.length > 0 && (
        <Card className="rounded-2xl border-accent/40 bg-accent/5 pop-in" data-testid="review-card">
          <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-accent/20 text-accent grid place-items-center shrink-0">
                <Zap size={20} />
              </div>
              <div>
                <h3 className="heading font-bold text-lg">Repaso personalizado</h3>
                <p className="text-sm text-foreground/70 mt-0.5">
                  {review.items.length} ejercicios recomendados según tus últimos fallos
                  {review.focus_domains?.length ? ` · Foco: ${review.focus_domains.map(d => d.replace(/_/g, " ")).join(", ")}` : ""}
                </p>
              </div>
            </div>
            <Link to="/review">
              <Button className="rounded-full h-11" data-testid="start-review-btn">
                Empezar repaso <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
        <Card className="rounded-2xl border-border">
          <CardHeader>
            <CardTitle className="heading">Perfil cognitivo por dominio</CardTitle>
            <CardDescription>
              Nivel actual en las 11 áreas de trabajo{preDone ? ", comparado con la evaluación PRE" : ""}
              {profile.post ? " y POST" : ""}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(liveDomains && liveDomains.total_attempts > 0) || preDone ? (
              <>
                <CognitiveRadar
                  live={liveDomains}
                  pre={profile.pre}
                  post={profile.post}
                />
                {liveDomains && (
                  <p className="text-xs text-muted-foreground mt-2 text-center" data-testid="radar-summary">
                    Basado en <b>{liveDomains.total_attempts}</b> intentos · acierto global{" "}
                    <b>{Math.round((liveDomains.overall_accuracy || 0) * 100)}%</b>
                  </p>
                )}
              </>
            ) : (
              <EmptyState msg="Aún no has completado ejercicios ni la evaluación PRE." />
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border">
          <CardHeader>
            <CardTitle className="heading">Dominios y severidad</CardTitle>
            <CardDescription>Clasificación por dominio (PRE).</CardDescription>
          </CardHeader>
          <CardContent>
            {preDone ? (
              <ul className="space-y-3" data-testid="domain-list">
                {profile.pre.scores.map((s) => (
                  <li key={s.domain} className="border border-border rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium">{DOMAIN_LABEL[s.domain] || s.domain}</span>
                      <Badge className={severityColor(s.severity)} data-testid={`severity-${s.domain}`}>
                        {SEVERITY_LABEL[s.severity]}
                      </Badge>
                    </div>
                    <Progress value={Math.round(s.accuracy * 100)} className="h-2" />
                    <div className="text-xs text-muted-foreground mt-1 flex justify-between">
                      <span>{Math.round(s.accuracy * 100)}%</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {Math.round(s.avg_response_ms)}ms</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState msg="Completa la evaluación PRE para ver esta información." />
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="heading text-2xl font-bold">Sesiones</h2>
          <Link to="/sessions" className="text-primary text-sm underline" data-testid="link-all-sessions">Ver todas</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {progress.slice(0, 6).map((s) => (
            <SessionMiniCard key={s.session_id} s={s} />
          ))}
        </div>
      </div>
    </div>
  );
}

const DOMAIN_LABEL = {
  emotion_recognition: "Recon. emocional",
  microexpressions: "Microexpresiones",
  emotional_prosody: "Prosodia emocional",
  mentalization: "Mentalización",
  theory_of_mind: "T. de la Mente",
  metacognition: "Metacognición",
  empathy_cognitive: "Empatía cognitiva",
  empathy_affective: "Empatía afectiva",
  social_inferences: "Inferencias soc.",
  non_literal_language: "Lenguaje no literal",
  interpersonal_regulation: "Reg. interpersonal",
};

function severityColor(sev) {
  return {
    preserved: "bg-primary/15 text-primary hover:bg-primary/20",
    mild: "bg-warning/20 text-foreground hover:bg-warning/30",
    moderate: "bg-accent/20 text-accent hover:bg-accent/30",
    severe: "bg-destructive/20 text-destructive hover:bg-destructive/30",
  }[sev] || "";
}

function Stat({ label, value, Icon, tone }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl grid place-items-center ${tone === "warn" ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="heading text-3xl font-extrabold mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function SessionMiniCard({ s }) {
  const pct = s.total_exercises ? Math.round((s.completed_exercises / s.total_exercises) * 100) : 0;
  const isMVPPlayable = true;
  return (
    <Card className="rounded-2xl border-border overflow-hidden" data-testid={`session-card-${s.session_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-primary font-semibold">Módulo {s.module}</div>
            <h3 className="heading text-lg font-bold mt-0.5">Sesión {s.session_id}</h3>
            <p className="text-sm text-foreground/70 mt-1">{s.title}</p>
          </div>
          {s.completed && <Badge className="bg-primary/15 text-primary">Hecho</Badge>}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Progress value={pct} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground">{s.completed_exercises}/{s.total_exercises}</span>
        </div>
        <div className="mt-4">
          {isMVPPlayable ? (
            <Link to={`/sessions/${s.session_id}`}>
              <Button size="sm" className="rounded-full" data-testid={`start-session-${s.session_id}`}>
                {s.completed_exercises > 0 ? "Continuar" : "Empezar"} <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          ) : (
            <Button size="sm" variant="outline" disabled className="rounded-full">
              Próximamente
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="text-center py-8 border border-dashed border-border rounded-xl">
      <Brain className="mx-auto text-muted-foreground mb-2" size={28} />
      <p className="text-sm text-muted-foreground">{msg}</p>
    </div>
  );
}
